import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import notification from 'utils/notificationBridge';
import { signOut } from 'store/slices/authSlice';
import {
  markListStaleForContentType,
  removePublishJob,
  updatePublishJobProgress,
} from 'redux/actions/PendingPublish';
import { clearJobFiles, getJobFiles } from 'helpers/publishJobFiles';
import { runPublishJob } from 'helpers/runPublishJob';
import { PUBLISH_CONTENT_TYPES } from 'helpers/publishContentTypes';
import {
  closeContentPublishNotification,
  getPublishFailedMessage,
  getPublishSuccessMessage,
  openContentPublishNotification,
} from 'helpers/contentPublishNotification';

const hasRequiredFiles = (job, files) => {
  switch (job.contentType) {
    case PUBLISH_CONTENT_TYPES.BANNER:
      if (job.mode === 'EDIT') {
        const hasWeb = files?.webFile || files?.existingWebImage;
        const hasMobile = files?.mobileFile || files?.existingMobileImage;
        return Boolean(hasWeb && hasMobile);
      }
      return Boolean(files?.webFile && files?.mobileFile);
    case PUBLISH_CONTENT_TYPES.TEMPLATE:
    case PUBLISH_CONTENT_TYPES.CATEGORIES:
      return true;
    case PUBLISH_CONTENT_TYPES.WIDGETS: {
      const widgetType = job.payload?.type;
      if (
        widgetType === 'Image Carousel' ||
        widgetType === 'Image Gallery'
      ) {
        return Array.isArray(files?.imageItems) && files.imageItems.length > 0;
      }
      if (widgetType === 'Exceptional Approach') {
        return (
          Array.isArray(files?.approachItems) && files.approachItems.length > 0
        );
      }
      return true;
    }
    case PUBLISH_CONTENT_TYPES.SEO:
    case PUBLISH_CONTENT_TYPES.INFORMATION_POLICY:
    case PUBLISH_CONTENT_TYPES.CONFIG_SETTINGS:
    case PUBLISH_CONTENT_TYPES.ADMIN_SETTINGS:
    case PUBLISH_CONTENT_TYPES.TAX_CATEGORY:
    case PUBLISH_CONTENT_TYPES.EMPLOYER:
    case PUBLISH_CONTENT_TYPES.BRANCH:
    case PUBLISH_CONTENT_TYPES.STAFF:
    case PUBLISH_CONTENT_TYPES.SHIFT:
    case PUBLISH_CONTENT_TYPES.TRANSACTION:
    case PUBLISH_CONTENT_TYPES.SHIFT_APPLICATION:
    case PUBLISH_CONTENT_TYPES.TIMESHEET:
    case PUBLISH_CONTENT_TYPES.STAFF_AVAILABILITY:
      return true;
    case PUBLISH_CONTENT_TYPES.STAFF_DOCUMENTS: {
      if (job.mode === 'EDIT') {
        return true;
      }
      // Match UNIVERSAL_DOCUMENT_KEYS — NI is number-only, not a file upload
      const requiredFiles = ['cvResume', 'photo'];
      const filesOk = requiredFiles.every(
        (key) => files?.[key]?.file || files?.[key]?.existingUrl,
      );
      const passportOk = Boolean(
        (files?.passport?.detailsPage?.file ||
          files?.passport?.detailsPage?.existingUrl ||
          files?.passport?.file ||
          files?.passport?.existingUrl) &&
          (files?.passport?.coverPage?.file ||
            files?.passport?.coverPage?.existingUrl),
      );
      const otherDocumentOk = Boolean(
        files?.otherDocument?.file || files?.otherDocument?.existingUrl,
      );
      const niNumber =
        job?.payload?.nationalInsurance?.nationalInsuranceNumber;
      return (
        filesOk &&
        passportOk &&
        otherDocumentOk &&
        Boolean(niNumber) &&
        Boolean(job?.payload?.otherDocument?.type)
      );
    }
    case PUBLISH_CONTENT_TYPES.PAGES:
      return Array.isArray(files?.imageItems) && files.imageItems.length > 0;
    default:
      return false;
  }
};

export const useContentPublishQueue = () => {
  const dispatch = useDispatch();
  const jobs = useSelector((state) => state.pendingPublish.jobs);
  const pendingJobId = jobs.find((job) => job.status === 'pending')?.id;
  const accessToken = useSelector((state) => state.auth.token);
  const activeJobIdRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!pendingJobId || !accessToken) {
      return;
    }

    if (activeJobIdRef.current) {
      return;
    }

    const pendingJob = jobs.find((job) => job.id === pendingJobId);
    if (!pendingJob) {
      return;
    }

    const files = getJobFiles(pendingJob.id);
    if (!hasRequiredFiles(pendingJob, files)) {
      dispatch(removePublishJob(pendingJob.id));
      clearJobFiles(pendingJob.id);
      notification.error({
        title: 'Error',
        description: 'Required files are missing.',
      });
      return;
    }

    activeJobIdRef.current = pendingJob.id;
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const showProgress = (phase, percent) => {
      openContentPublishNotification({
        jobId: pendingJob.id,
        title: pendingJob.title,
        contentType: pendingJob.contentType,
        phase,
        percent,
      });
    };

    dispatch(
      updatePublishJobProgress({
        id: pendingJob.id,
        status: 'processing',
        phase: 'queued',
        percent: 0,
      }),
    );
    showProgress('queued', 0);

    runPublishJob({
      job: pendingJob,
      files,
      accessToken,
      signal: controller.signal,
      onProgress: ({ phase, percent, status }) => {
        dispatch(
          updatePublishJobProgress({
            id: pendingJob.id,
            phase,
            percent,
            status: status || 'processing',
          }),
        );
        showProgress(phase, percent);
      },
    })
      .then(() => {
        closeContentPublishNotification(pendingJob.id);
        clearJobFiles(pendingJob.id);
        dispatch(removePublishJob(pendingJob.id));
        dispatch(markListStaleForContentType(pendingJob.contentType));
        notification.success({
          title: 'Success',
          description: getPublishSuccessMessage(
            pendingJob.contentType,
            pendingJob.mode,
          ),
        });
      })
      .catch((error) => {
        if (error?.name === 'AbortError') {
          closeContentPublishNotification(pendingJob.id);
          return;
        }

        closeContentPublishNotification(pendingJob.id);

        if (error?.statusCode === 401) {
          dispatch(signOut());
          return;
        }

        clearJobFiles(pendingJob.id);
        dispatch(removePublishJob(pendingJob.id));
        notification.error({
          title: 'Error',
          description:
            error?.message ||
            getPublishFailedMessage(pendingJob.contentType, pendingJob.mode),
        });
      })
      .finally(() => {
        if (activeJobIdRef.current === pendingJob.id) {
          activeJobIdRef.current = null;
        }
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      });
  }, [accessToken, dispatch, jobs, pendingJobId]);
};
